/** @module Decides when the card in the guide frame sits still enough to shoot by itself. */

export interface FrameStats {
  /** Average brightness change per pixel since the previous frame, 0 to 255. */
  motion: number;
  /**
   * How far the frame differs from the remembered empty surface, when one was taken.
   *
   * With the phone in a holder the background never changes, so "is something lying
   * there" stops being a guess: the keyboard, the cloth and the table are the
   * background, and only what is put on top of them counts.
   */
  novelty?: number;
  /**
   * How far everything around the guide frame differs from the moment of the last photo.
   *
   * Small means the camera has not moved (a scan box, a holder): then the same card always
   * looks the same, and a much smaller change already means the next card.
   */
  surround?: number;
  /** Average edge strength; an empty table has almost none, a card has plenty. */
  detail: number;
  /** How far the inside of the guide frame stands out from everything around it. */
  contrast: number;
  /** How different the frame looks from the last photo; absent before the first one. */
  change?: number;
}

/** A rectangle in pixels of the sampled thumbnail. */
export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * A fixed threshold cannot work here.
 *
 * Measured on a phone: a card held as still as a hand can hold it still moves by 12 to
 * 23, the same card being taken away by 30 to 68. Those numbers shift with the hand,
 * the distance and the light, so "still" means "close to the quietest this scene has
 * just been" instead of a number picked in advance.
 */
const WINDOW = 16;
const MARGIN = 6;
/** However quiet the baseline, this much change is always someone moving the card. */
const MOTION_CEILING = 28;
/** Under this there is nothing card-like in the frame, just an empty surface. */
const MIN_DETAIL = 6;
/**
 * Structure alone is not a card.
 *
 * A keyboard, a patterned table or a printed page fill the frame with edges and hold
 * perfectly still, and the shutter fired on all of them. A card also has to *stand
 * out* from what surrounds it, in brightness or in texture.
 */
const MIN_CONTRAST = 12;
/** Frames the picture has to stay still by default; at roughly 8 fps this is half a second. */
export const STEADY_FRAMES = 4;
/** After a shot the detector is blind this long, so one card cannot be shot twice. */
export const COOLDOWN_MS = 1200;
/**
 * A fingerprint this far from the last photo means a different card lies in the frame.
 *
 * Measured on real shots: the same card moved by up to 6 % stays below 27, two
 * different cards start at 34. Without this the detector waited for an empty frame
 * between two cards, and a hand in the picture while swapping kept it waiting.
 */
export const REARM_CHANGE = 30;
/**
 * Below this a photo looks like the one before it: the same card, or a second copy of it.
 *
 * Measured 2026-10-04 in a scan box: the same card again 2.4 to 9.5, the next different
 * card from 12.4 up. Only a hint for the person, never a block: second copies are real.
 */
export const SAME_CARD_CHANGE = 11;
/**
 * Below this the surroundings are those of the last photo: the camera stands still.
 *
 * The same bound as "nothing new on the remembered surface"; camera noise and exposure
 * stay well under it, a moved phone or a hand in the picture goes far above.
 */
export const FIXED_CAMERA = 8;
/**
 * Difference from the remembered surface that counts as "something lies there".
 *
 * Camera noise and the automatic exposure move an untouched scene by one or two; a card
 * covering most of the frame moves it by tens.
 */
export const MIN_NOVELTY = 8;

/** Turn an RGBA frame into grayscale bytes; the weights follow human brightness. */
export function toGray(pixels: Uint8ClampedArray): Uint8Array {
  const gray = new Uint8Array(pixels.length / 4);
  for (let index = 0; index < gray.length; index += 1) {
    const offset = index * 4;
    gray[index] =
      0.299 * (pixels[offset] ?? 0) +
      0.587 * (pixels[offset + 1] ?? 0) +
      0.114 * (pixels[offset + 2] ?? 0);
  }
  return gray;
}

/**
 * How much the picture itself changed, ignoring a global change in brightness.
 *
 * The automatic exposure of a phone lifts or drops every pixel at once; counting that
 * as movement would keep the camera waiting forever. Subtracting the average shift
 * leaves what actually moved.
 */
export function motionBetween(previous: Uint8Array, current: Uint8Array): number {
  if (previous.length !== current.length || current.length === 0) return 255;

  let shift = 0;
  for (let index = 0; index < current.length; index += 1) {
    shift += (current[index] ?? 0) - (previous[index] ?? 0);
  }
  shift /= current.length;

  let total = 0;
  for (let index = 0; index < current.length; index += 1) {
    total += Math.abs((current[index] ?? 0) - (previous[index] ?? 0) - shift);
  }
  return total / current.length;
}

/** Mean gradient magnitude: card art and text have it, a bare table does not. */
export function detailOf(gray: Uint8Array, width: number, height: number): number {
  if (width < 2 || height < 2) return 0;
  let total = 0;
  let counted = 0;
  for (let y = 1; y < height; y += 1) {
    for (let x = 1; x < width; x += 1) {
      const here = gray[y * width + x] ?? 0;
      const left = gray[y * width + x - 1] ?? 0;
      const above = gray[(y - 1) * width + x] ?? 0;
      total += Math.abs(here - left) + Math.abs(here - above);
      counted += 1;
    }
  }
  return counted === 0 ? 0 : total / counted / 2;
}

/** Cut a rectangle out of the sampled thumbnail. */
/** Every pixel outside the box, row by row; the same box always gives the same length. */
export function outside(gray: Uint8Array, width: number, box: Box): Uint8Array {
  const out = new Uint8Array(gray.length - box.width * box.height);
  let at = 0;
  const height = gray.length / width;
  for (let y = 0; y < height; y += 1) {
    const row = gray.subarray(y * width, (y + 1) * width);
    if (y < box.y || y >= box.y + box.height) {
      out.set(row, at);
      at += width;
      continue;
    }
    out.set(row.subarray(0, box.x), at);
    at += box.x;
    out.set(row.subarray(box.x + box.width), at);
    at += width - box.x - box.width;
  }
  return out;
}

export function crop(gray: Uint8Array, width: number, box: Box): Uint8Array {
  const out = new Uint8Array(box.width * box.height);
  for (let y = 0; y < box.height; y += 1) {
    const from = (box.y + y) * width + box.x;
    out.set(gray.subarray(from, from + box.width), y * box.width);
  }
  return out;
}

function inset(box: Box, share: number): Box {
  const dx = box.width * share;
  const dy = box.height * share;
  return {
    x: box.x + dx,
    y: box.y + dy,
    width: box.width - 2 * dx,
    height: box.height - 2 * dy,
  };
}

function contains(box: Box, x: number, y: number): boolean {
  return x >= box.x && x < box.x + box.width && y >= box.y && y < box.y + box.height;
}

/** Share of the frame ignored on either side, so a slightly tilted card still counts. */
const INNER_MARGIN = 0.1;
const OUTER_MARGIN = 0.06;

/** Running mean of brightness and of edge strength for one side of the frame. */
class Side {
  private sum = 0;
  private pixels = 0;
  private edges = 0;
  private gradients = 0;

  add(value: number): void {
    this.sum += value;
    this.pixels += 1;
  }

  addEdge(strength: number): void {
    this.edges += strength;
    this.gradients += 1;
  }

  get empty(): boolean {
    return this.pixels === 0;
  }

  get brightness(): number {
    return this.pixels === 0 ? 0 : this.sum / this.pixels;
  }

  get detail(): number {
    return this.gradients === 0 ? 0 : this.edges / this.gradients / 2;
  }
}

/**
 * How different the guide frame looks from its surroundings.
 *
 * Two things can set a card apart: it is brighter or darker than the table, or it
 * carries print where the table has none. Either is enough; a scene that looks the
 * same inside and outside the frame holds no card, however much structure it has.
 */
export function contrastAround(
  gray: Uint8Array,
  width: number,
  height: number,
  guide: Box,
): number {
  const inner = inset(guide, INNER_MARGIN);
  const outer = inset(guide, -OUTER_MARGIN);
  const sides = { in: new Side(), out: new Side() };
  // Between the two boxes lies the card's own edge; it belongs to neither side.
  const sideAt = (x: number, y: number) =>
    contains(inner, x, y) ? sides.in : contains(outer, x, y) ? null : sides.out;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const side = sideAt(x, y);
      if (!side) continue;
      const here = gray[y * width + x] ?? 0;
      side.add(here);
      // A gradient only counts where both neighbours are on the same side, so the
      // card's own edge does not show up as texture of the table.
      if (x === 0 || y === 0) continue;
      if (sideAt(x - 1, y) !== side || sideAt(x, y - 1) !== side) continue;
      side.addEdge(
        Math.abs(here - (gray[y * width + x - 1] ?? 0)) +
          Math.abs(here - (gray[(y - 1) * width + x] ?? 0)),
      );
    }
  }

  if (sides.in.empty || sides.out.empty) return 0;
  const brightness = Math.abs(sides.in.brightness - sides.out.brightness);
  const texture = Math.abs(sides.in.detail - sides.out.detail);
  return Math.max(brightness, 2 * texture);
}

/** Grid of the fingerprint; coarse enough that a trembling hand does not change it. */
const PRINT_COLUMNS = 6;
const PRINT_ROWS = 8;
/** Below this spread a channel is flat; dividing by it would only amplify noise. */
const MIN_SPREAD = 4;

/**
 * A rough picture of what lies in the frame: 6 x 8 cells, colour, brightness removed.
 *
 * Colour matters: two cards that look alike in grey rarely do in colour. Each channel
 * is normalised, so a change in exposure does not look like a new card.
 */
export function fingerprintOf(pixels: Uint8ClampedArray, width: number, box: Box): Float32Array {
  const sums = new Float64Array(PRINT_COLUMNS * PRINT_ROWS * 3);
  const counts = new Uint32Array(PRINT_COLUMNS * PRINT_ROWS);
  for (let y = 0; y < box.height; y += 1) {
    const row = Math.min(PRINT_ROWS - 1, Math.floor((y * PRINT_ROWS) / box.height));
    for (let x = 0; x < box.width; x += 1) {
      const column = Math.min(PRINT_COLUMNS - 1, Math.floor((x * PRINT_COLUMNS) / box.width));
      const cell = row * PRINT_COLUMNS + column;
      const offset = ((box.y + y) * width + box.x + x) * 4;
      sums[cell * 3] = (sums[cell * 3] ?? 0) + (pixels[offset] ?? 0);
      sums[cell * 3 + 1] = (sums[cell * 3 + 1] ?? 0) + (pixels[offset + 1] ?? 0);
      sums[cell * 3 + 2] = (sums[cell * 3 + 2] ?? 0) + (pixels[offset + 2] ?? 0);
      counts[cell] = (counts[cell] ?? 0) + 1;
    }
  }

  const cells = PRINT_COLUMNS * PRINT_ROWS;
  const print = new Float32Array(cells * 3);
  for (let channel = 0; channel < 3; channel += 1) {
    let mean = 0;
    for (let cell = 0; cell < cells; cell += 1) {
      const value = (sums[cell * 3 + channel] ?? 0) / Math.max(1, counts[cell] ?? 0);
      print[channel * cells + cell] = value;
      mean += value;
    }
    mean /= cells;
    let spread = 0;
    for (let cell = 0; cell < cells; cell += 1) {
      spread += ((print[channel * cells + cell] ?? 0) - mean) ** 2;
    }
    spread = Math.max(MIN_SPREAD, Math.sqrt(spread / cells));
    for (let cell = 0; cell < cells; cell += 1) {
      const index = channel * cells + cell;
      print[index] = (((print[index] ?? 0) - mean) / spread) * 40;
    }
  }
  return print;
}

/** Mean difference of two fingerprints; see REARM_CHANGE for what the numbers mean. */
export function fingerprintDistance(a: Float32Array, b: Float32Array): number {
  if (a.length !== b.length || a.length === 0) return Number.POSITIVE_INFINITY;
  let total = 0;
  for (let index = 0; index < a.length; index += 1) {
    total += Math.abs((a[index] ?? 0) - (b[index] ?? 0));
  }
  return total / a.length;
}

/** `taken` means the shot is done and the card has to leave the frame first. */
export type Readiness = "empty" | "taken" | "moving" | "settling" | "ready";

/** What armed the shutter for the card it just took; logged to tell doubles from swaps. */
export interface Rearm {
  by: "start" | "empty" | "change" | "fixed";
  /** Empty frames in a row before the card appeared; a hand passing by gives one or two. */
  emptyFrames: number;
}

export class SteadyFrame {
  /**
   * A hand that just put the card down lies still on it too; waiting longer lets the
   * fingers leave the frame before the shot (half a second was too short in practice).
   */
  constructor(private readonly steadyFrames: number = STEADY_FRAMES) {}

  private recent: number[] = [];
  private steady = 0;
  private lastShot: number | null = null;
  /**
   * After a shot the card usually stays in the frame for a moment.
   *
   * A cooldown alone would fire again the second it passes, so the detector arms only
   * once the frame is empty again: shoot, take the card away, put the next one down.
   */
  private armed = true;
  private state: Readiness = "empty";
  private emptyRun = 0;
  private lastEmptyRun = 0;
  private armedBy: Rearm["by"] = "start";

  /** Whether the frame holds something worth a photo. */
  private occupied(stats: FrameStats): boolean {
    // A remembered surface beats every heuristic: it knows this exact scene when empty.
    if (stats.novelty !== undefined) return stats.novelty >= MIN_NOVELTY;
    return stats.detail >= MIN_DETAIL && stats.contrast >= MIN_CONTRAST;
  }

  /** Feed one frame; returns true exactly once when it is time to take the photo. */
  accept(stats: FrameStats, now: number = Date.now()): boolean {
    if (!this.occupied(stats)) {
      this.steady = 0;
      this.recent = [];
      this.state = "empty";
      this.emptyRun += 1;
      if (!this.armed && (this.lastShot === null || now - this.lastShot >= COOLDOWN_MS)) {
        this.armed = true;
        this.armedBy = "empty";
      }
      return false;
    }
    if (this.emptyRun > 0) {
      this.lastEmptyRun = this.emptyRun;
      this.emptyRun = 0;
    }
    if (!this.armed) {
      // A different card needs no empty frame first: swapping with a hand in view must
      // not leave the shutter waiting for a moment of bare table that never comes.
      const cooled = this.lastShot === null || now - this.lastShot >= COOLDOWN_MS;
      // A fast swap in a scan box never shows an empty frame, and two similar cards stay
      // under REARM_CHANGE; with the camera fixed, the same card never gets past 11.
      const fixed = stats.surround !== undefined && stats.surround < FIXED_CAMERA;
      const change = stats.change ?? 0;
      const different = change >= REARM_CHANGE || (fixed && change >= SAME_CARD_CHANGE);
      if (!cooled || !different) {
        this.state = "taken";
        return false;
      }
      this.armed = true;
      this.armedBy = change >= REARM_CHANGE ? "change" : "fixed";
      this.steady = 0;
      this.recent = [];
    }
    this.recent.push(stats.motion);
    if (this.recent.length > WINDOW) this.recent.shift();
    const quietest = Math.min(...this.recent);

    if (stats.motion > Math.min(quietest + MARGIN, MOTION_CEILING)) {
      this.steady = 0;
      this.state = "moving";
      return false;
    }

    this.steady += 1;
    if (this.steady < this.steadyFrames) {
      this.state = "settling";
      return false;
    }

    this.state = "ready";
    this.lastShot = now;
    this.steady = 0;
    this.armed = false;
    return true;
  }

  /**
   * A photo was taken by hand: the card in the frame counts as done.
   *
   * Without this the shutter took the same card again a second after the button did.
   */
  markTaken(now: number = Date.now()): void {
    this.lastShot = now;
    this.steady = 0;
    this.armed = false;
    this.state = "taken";
  }

  /** How the shutter got armed for the current card. */
  get rearm(): Rearm {
    return { by: this.armedBy, emptyFrames: this.lastEmptyRun };
  }

  /** What the overlay should tell the person right now. */
  get readiness(): Readiness {
    return this.state;
  }

  /** How close it is to shooting, between 0 and 1. */
  get progress(): number {
    return Math.min(1, this.steady / this.steadyFrames);
  }

  /** The quietest the scene has been lately; shown in the diagnostics line. */
  get baseline(): number {
    return this.recent.length === 0 ? 0 : Math.min(...this.recent);
  }

  reset(): void {
    this.recent = [];
    this.steady = 0;
    this.armed = true;
    this.armedBy = "start";
    this.state = "empty";
  }
}
